import { BucketUnit, FilterGroup, QueryFilter } from "@nova/queryable"
import { EventAction, EventCategory, EventKind, eventTaxonomy } from "@nova/schemas"
import {
    DatePreset,
    DateRangeValue,
    isValidDateRange,
    resolveDateRange,
} from "features/filters/dateRange"

export type DateFilter = DateRangeValue
export type { DatePreset }

export type CategorySelection = {
    actions: EventAction[]
    subjectTypes: string[]
}

export type ActivityFilters = {
    date: DateFilter
    people: string[]
    categories: Partial<Record<EventCategory, CategorySelection>>
}

export const emptyCategorySelection: CategorySelection = { actions: [], subjectTypes: [] }

const defaultDatePreset: DatePreset = "30d"

export const defaultActivityFilters: ActivityFilters = {
    date: { preset: defaultDatePreset },
    people: [],
    categories: {},
}

export { isValidDateRange as isValidDateFilter, resolveDateRange }

const DAY_MS = 24 * 60 * 60 * 1000

const isSameLocalDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

export const bucketUnitFor = (start: Date, end: Date): BucketUnit => {
    if (isSameLocalDay(start, end)) return "hour"
    const days = (end.getTime() - start.getTime()) / DAY_MS
    if (days <= 31) return "day"
    if (days <= 180) return "week"
    return "month"
}

const allKinds = Object.keys(eventTaxonomy) as EventKind[]

export const kindsFor = (category: EventCategory, actions: EventAction[]) =>
    allKinds.filter((kind) => {
        const entry = eventTaxonomy[kind]
        if (entry.category !== category) return false
        return actions.length === 0 || actions.includes(entry.action)
    })

export const actionsFor = (category: EventCategory) => [
    ...new Set(
        allKinds
            .filter((kind) => eventTaxonomy[kind].category === category)
            .map((kind) => eventTaxonomy[kind].action)
    ),
]

const isDefaultDate = (date: DateFilter) => "preset" in date && date.preset === defaultDatePreset

export const activeFilterCount = (filters: ActivityFilters) =>
    Object.keys(filters.categories).length +
    (filters.people.length > 0 ? 1 : 0) +
    (isDefaultDate(filters.date) ? 0 : 1)

export const compileActivityFilters = (filters: ActivityFilters) => {
    const { start, end } = resolveDateRange(filters.date)

    const baseFilters: QueryFilter<"event_logs">[] = [
        { field: "created_at", op: ">=", value: start.toISOString() },
        { field: "created_at", op: "<=", value: end.toISOString() },
    ]

    if (filters.people.length > 0) {
        baseFilters.push({ field: "created_by", op: "IN", value: filters.people })
    }

    const filterGroups: FilterGroup<"event_logs">[] = Object.entries(filters.categories).map(
        ([category, selection]) => {
            const groupFilters: QueryFilter<"event_logs">[] = [
                {
                    field: "kind",
                    op: "IN",
                    value: kindsFor(category as EventCategory, selection.actions),
                },
            ]

            if (selection.subjectTypes.length > 0) {
                groupFilters.push({
                    field: "subject_type",
                    op: "IN",
                    value: selection.subjectTypes,
                })
            }

            return { filters: groupFilters }
        }
    )

    return { baseFilters, filterGroups, start, end, unit: bucketUnitFor(start, end) }
}
